'use client';

import { navLinks } from '@/components/Header/helpers';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { useToggle } from 'react-use';
import { twMerge } from 'tailwind-merge';
import s from './MobileMenu.module.scss';

export const MobileMenu = ({ className }: { className: string }) => {
  const currentRoute = usePathname();
  const [open, toggleOpen] = useToggle(false);

  const isInIframe = useMemo(() => {
    if (typeof window === 'undefined') return true;
    return window.self !== window.top;
  }, [window]);

  const preparedNavLinks = useMemo(() => {
    const result: Array<{
      parent?: string;
      text: string;
      href?: string;
      disabled?: boolean;
      show?: boolean;
      target?: string;
      isParent?: boolean;
    }> = [];
    navLinks(isInIframe).forEach((link) => {
      if (link.show) {
        if (link.children && link.children.length) {
          result.push({
            text: link.text,
            isParent: true,
          });
          link.children.forEach((child) => {
            result.push({ ...child, parent: link.text });
          });
        } else {
          result.push(link);
        }
      }
    });
    return result;
  }, [isInIframe]);

  return (
    <div
      className={clsx(twMerge(className), {
        [s.open]: open,
      })}
    >
      <div className="p-4" onClick={toggleOpen}>
        <svg className={s.burger}>
          <line x1={0} x2={24} y1={4} y2={4} />
          <line x1={0} x2={24} y1={12} y2={12} />
          <line x1={0} x2={24} y1={20} y2={20} />
        </svg>
      </div>
      <div
        className={twMerge(
          `transition-transform overflow-x-hidden duration-500 ease-in-out transform translate-y-full overflow-y-auto overscroll-contain touch-pan-y w-full h-[calc(100vh-80px)] bg-neutral-950 fixed top-[80px] left-0 flex flex-col px-6 pb-[80px] gap-4`,
          open && 'translate-y-0'
        )}
      >
        {preparedNavLinks.map(({ text, href, disabled, show, target, isParent, parent }, ix) => {
          if (isParent) {
            return (
              <div key={`parent-${text}-${ix}`} className="text-neutral-400 font-bold text-base mt-4 mb-1 pl-4">
                {text}
              </div>
            );
          }
          if (show !== false) {
            if (disabled) {
              return (
                <div key={`${text}-${ix}`} className="text-neutral-100 font-medium opacity-40 pointer-events-none ml-4">
                  {text}
                </div>
              );
            }

            if (href) {
              return (
                <Link
                  key={text}
                  href={href}
                  target={target}
                  className={`flex items-center w-full text-neutral-100 font-medium text-lg px-4 py-1 rounded-md ml-${parent ? '4' : '0'}
                        ${currentRoute === href ? 'text-primary bg-neutral-900' : ''}`}
                  onClick={toggleOpen}
                >
                  {text}
                </Link>
              );
            }
          }
          return null;
        })}
      </div>
    </div>
  );
};
